import { Fragment, type ReactNode } from "react";
import type { AddressValue, FieldSpec, FieldValue, FormValues, ScalarFieldSpec } from "../types.js";
import { CHOICE_FIELD_TYPES } from "../types.js";
import type { LookupResult } from "../api/formApi.js";
import { asRows, asString, fieldLabel, isGroupField, lockedTokenLabel, optionEntries, readOnlyShown, toneFor, visibleFields } from "../schema/formModel.js";
import { sameAsSource } from "../schema/formDesign.js";
import { formatHints } from "../schema/formats.js";
import type { PredicateScope } from "../schema/predicates.js";
import { FieldRow, describedByIds } from "./FieldRow.js";
import { GroupField } from "./GroupField.js";
import { ReadOnlyValue } from "./ReadOnlyValue.js";
import { ScalarInput, SecretInput, rowVariantFor } from "./ScalarInput.js";
import "./styles.css";

export interface FormRendererProps {
  fields?: FieldSpec[];
  values: FormValues;
  errors: Record<string, string>;
  onChange: (name: string, value: FieldValue) => void;
  /** KYC-design — the page validates a field when its control loses focus. */
  onBlur?: (name: string) => void;
  scope: PredicateScope;
  /** A per-field slot rendered INSIDE the row beside the control (the lookup button). */
  fieldAction?: (field: FieldSpec) => ReactNode;
  /** A per-field slot rendered after the row (the lookup result card). */
  fieldSlot?: (field: FieldSpec) => ReactNode;
  /**
   * Card #2717 — the trigger fields of this step's LIST-ONLY lookups (`list_only: true`). Each
   * renders as the HEADING of its lookup's list — label and help, then the list ({@link fieldSlot}),
   * then the lookup's own control ({@link fieldAction}) — and NO input box, because nothing reads
   * what would be typed there. ABSENT/empty ⇒ every field renders exactly as before.
   */
  listOnlyFields?: ReadonlySet<string>;
  /** Fields a lookup filled in, with the note to show ("From the register"). */
  prefilled?: Readonly<Record<string, string>>;
  /** Group rows a lookup filled in, keyed by group name. */
  prefilledRows?: Readonly<Record<string, ReadonlySet<number>>>;
  /**
   * Phase 541 — the ADDRESS-LOOKUP transport, handed straight down to every address block on the
   * step: the top-level ones here, the group SUBFIELD ones through {@link GroupField}.
   *
   * <p>It is passed through and never called here, and it carries no per-field key: the busy /
   * candidate state belongs to each `AddressInput` instance, so two rows of a repeating group are
   * independent because they are two React instances. ABSENT (the page passes nothing when no
   * field on the step declares `lookup`) ⇒ no address block renders a search control, exactly as
   * before this phase.</p>
   */
  onAddressLookup?: (postcode: string) => Promise<LookupResult>;
}

/**
 * The step's fields as a grid: every row spans the grid unless it is `width: half`, which
 * pairs it with its neighbour at desktop widths.
 */
export function FormRenderer({ fields, values, errors, onChange, onBlur, scope, fieldAction, fieldSlot, listOnlyFields, prefilled, prefilledRows, onAddressLookup }: FormRendererProps) {
  const visible = visibleFields(fields, scope);
  if (visible.length === 0) return null;
  return (
    <div className="field-grid">
      {visible.map((f) => {
        const label = fieldLabel(f);
        const error = errors[f.name];
        const slot = fieldSlot?.(f) ?? null;
        if (isGroupField(f)) {
          const note = prefilled?.[f.name];
          return (
            <Fragment key={f.name}>
              <GroupField
                spec={f}
                rows={asRows(values[f.name])}
                errors={errors}
                onRowsChange={(rows) => onChange(f.name, rows)}
                scope={scope}
                prefilledRows={prefilledRows?.[f.name]}
                prefilledNote={note}
                onAddressLookup={onAddressLookup}
                // Read only by a group declaring `self_match` (its filler is a top-level answer).
                formValues={values}
              />
              {slot}
            </Fragment>
          );
        }
        const scalar = f as ScalarFieldSpec;
        // Card #2878 — a HIDDEN field (a LEAP id a pick fills beside the name it shows) renders
        // nothing at all; its value still rides the submission like any other answer.
        if (scalar.hidden === true) return null;
        const action = fieldAction?.(f) ?? null;

        // Card #2717 — a LIST-ONLY lookup's trigger field: the heading of the list, never an input.
        // A fieldset + legend rather than a label, because there is no control for a label to name;
        // the list and the lookup's own control (re-run if the list failed to load) sit inside it.
        // It is not a question anyone answers, so it is neither required nor optional:
        // `required={false}` (nothing to answer) with `optionalSuffix={false}` (no "(optional)").
        if (listOnlyFields?.has(f.name) === true) {
          return (
            <FieldRow key={f.name} name={f.name} label={label} required={false} optionalSuffix={false} error={error} help={f.help} helpLink={f.help_link} variant="group" width={scalar.width}>
              {slot}
              {action}
            </FieldRow>
          );
        }
        const note = prefilled?.[f.name];
        const sameAs = scalar.type === "address" ? sameAsSource(scalar, values, fields) : null;
        // Derived from the CURRENT value, so the read-back follows what the subject types and
        // what a lookup fills in alike — there is only one value, so there is only one rule.
        const hints = formatHints(scalar.format, values[f.name]);
        const describedBy = describedByIds(f.name, { help: f.help, error, hints });

        // Phase 572 wave 3 task 3.2 — a locked field renders as a fixed fact through ReadOnlyValue.
        // The value is from the existing from_ctx_value mechanism (the pre-answer), and for choice
        // fields it is shown as the LABEL rather than the wire token. Not validated as an input.
        // `required={true}` hides the "(optional)" suffix — a locked field is neither required nor
        // optional, it is a fact, and "required to have a value" is accurate since it does.
        if (scalar.locked) {
          const rawValue = asString(values[f.name]);
          const displayValue = CHOICE_FIELD_TYPES.includes(scalar.type)
            ? optionEntries(scalar).find((e) => e.value === rawValue)?.label ?? lockedTokenLabel(rawValue)
            : rawValue;
          return (
            <Fragment key={f.name}>
              <FieldRow
                name={f.name}
                label={label}
                required={true}
                help={f.help}
                helpLink={f.help_link}
                variant="field"
                width={scalar.width}
              >
                {/* Walk 2 — the gate's declared `tones` colour the fixed value (Final risk band). */}
                <ReadOnlyValue value={displayValue} sentence={scalar.locked_sentence} tone={toneFor(scalar, rawValue)} />
                {note !== undefined && <span className="chip">{note}</span>}
              </FieldRow>
              {slot}
            </Fragment>
          );
        }

        // Phase 572 wave 6 task 6.2 — a DECLARED read_only field renders through the SAME
        // ReadOnlyValue as a locked one (Reconciliation R3): its value, already formatted
        // (described lines, the address block, or plain text with choices as their LABEL), inside
        // the ordinary FieldRow with the source chip beside it. With NO value the whole row is
        // absent — no label, no chip. The answer itself is untouched, so the payload is too.
        if (scalar.read_only === true) {
          const shown = readOnlyShown(scalar, values);
          // BB walk 2 — a REQUIRED read_only field (filled by a choose pick) that is still empty
          // shows its label and the "confirm your match" error where the value will appear, so
          // a blocked Continue says why. No control: the pick above is the control.
          if (shown === null && error !== undefined) {
            return (
              <Fragment key={f.name}>
                <FieldRow name={f.name} label={label} required={true} error={error} variant="field" width={scalar.width}>
                  {null}
                </FieldRow>
                {slot}
              </Fragment>
            );
          }
          if (shown === null) return null;
          return (
            <Fragment key={f.name}>
              <FieldRow
                name={f.name}
                label={label}
                required={true}
                help={f.help}
                helpLink={f.help_link}
                variant="field"
                width={scalar.width}
              >
                <ReadOnlyValue id={f.name} {...shown} />
                {note !== undefined && <span className="chip">{note}</span>}
              </FieldRow>
              {slot}
            </Fragment>
          );
        }

        const control = scalar.sensitive ? (
          <SecretInput
            spec={scalar}
            id={f.name}
            value={values[f.name]}
            invalid={error !== undefined}
            describedBy={describedBy}
            onChange={(value) => onChange(f.name, value)}
            onBlur={() => onBlur?.(f.name)}
          />
        ) : (
          <ScalarInput
            spec={scalar}
            id={f.name}
            value={values[f.name]}
            invalid={error !== undefined}
            describedBy={describedBy}
            onChange={(value) => onChange(f.name, value as FieldValue | AddressValue)}
            onBlur={() => onBlur?.(f.name)}
            sameAs={sameAs}
            prefilled={note !== undefined && scalar.type === "address"}
            prefilledNote={note}
            onAddressLookup={onAddressLookup}
          />
        );
        return (
          <Fragment key={f.name}>
            <FieldRow
              name={f.name}
              label={label}
              required={f.required ?? false}
              error={error}
              help={f.help}
              helpLink={f.help_link}
              hints={hints}
              variant={rowVariantFor(scalar.type)}
              width={scalar.width}
            >
              {action ? <div className="field-with-action">{control}{action}</div> : control}
              {note !== undefined && scalar.type !== "address" && <span className="chip">{note}</span>}
            </FieldRow>
            {slot}
          </Fragment>
        );
      })}
    </div>
  );
}
